import React from 'react';
import { Badge, Box, Button, Flex, Heading, Text } from '@chakra-ui/react';
import { CaretLeft, ClipboardText } from 'phosphor-react';

import { IContratoSocietario } from '../../../../../hooks/useSocietario';
import { estadoDoCard, formatarDataHora, situacaoDoCard } from '../../listagem';

const ROTULO_DA_OPERACAO: Record<string, string> = {
  constituicao: 'Constituição',
  alteracao: 'Alteração contratual',
  transformacao: 'Transformação',
};

interface CabecalhoDoWizardProps {
  contrato: IContratoSocietario | null;
  /** Operação escolhida no form (antes de existir contrato, é a única fonte). */
  tipoOperacao?: string | null;
  /** Razão social do form: o título acompanha o que o operador digita. */
  razaoSocial?: string | null;
  /** Sai do wizard (a página confirma antes de descartar um rascunho). */
  onSair: () => void;
  /** Linha extra abaixo do título (ex.: tela de sucesso). */
  complemento?: string;
}

// Cabeçalho no padrão dos submódulos (faixa branca, ícone do módulo, sobretítulo,
// título). O "‹ Contratos" substitui o "Voltar" do topo: havia dois "Voltar" na tela,
// um saía do wizard e o outro voltava etapa (28/09).
export const CabecalhoDoWizard: React.FC<CabecalhoDoWizardProps> = ({
  contrato,
  tipoOperacao,
  razaoSocial,
  onSair,
  complemento,
}) => {
  const operacao = contrato?.tipo_operacao ?? tipoOperacao ?? '';
  const rotuloDaOperacao =
    operacao === 'alteracao' && contrato?.numero_alteracao
      ? `${contrato.numero_alteracao}ª alteração`
      : ROTULO_DA_OPERACAO[operacao];
  const sobretitulo = [contrato ? `Contrato #${contrato.id}` : 'Novo contrato', rotuloDaOperacao]
    .filter(Boolean)
    .join(' · ');
  const titulo = razaoSocial?.trim() || contrato?.razao_social || 'Novo contrato societário';
  const situacao = contrato ? situacaoDoCard(contrato, estadoDoCard(contrato)) : null;

  return (
    <Box bg="white" borderBottomWidth="1px" borderColor="gray.100" px={{ base: 4, md: 8 }} py={4}>
      <Box maxW="1280px" mx="auto">
        <Button
          variant="ghost"
          size="sm"
          h="28px"
          px={2}
          ml={-2}
          mb={2}
          fontSize="13px"
          fontWeight="600"
          color="gray.500"
          leftIcon={<CaretLeft size={14} weight="bold" />}
          _hover={{ color: 'gray.800', bg: 'gray.50' }}
          onClick={onSair}
          data-testid="sair-do-wizard"
        >
          Contratos
        </Button>
        <Flex align="center" gap={3}>
          <Box
            w="48px"
            h="48px"
            borderRadius="xl"
            bg="#DBEAFE"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <ClipboardText size={26} color="#1976D2" weight="duotone" />
          </Box>
          <Box minW={0}>
            <Text
              fontSize="10px"
              color="gray.400"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.1em"
            >
              {sobretitulo}
            </Text>
            <Heading
              fontSize={{ base: 'lg', md: 'xl' }}
              fontWeight="700"
              color="gray.900"
              letterSpacing="-0.02em"
              noOfLines={1}
              data-testid="titulo-do-wizard"
            >
              {titulo}
            </Heading>
            <Flex gap={2} mt={1} align="center" wrap="wrap" fontSize="xs" color="gray.500">
              {situacao && (
                <Badge
                  colorScheme={situacao.cor}
                  variant={situacao.solido ? 'solid' : 'subtle'}
                  fontSize="9px"
                  borderRadius="full"
                  px={2}
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  {situacao.rotulo}
                </Badge>
              )}
              {contrato?.dt_criacao && (
                <Text>Criado em {formatarDataHora(contrato.dt_criacao)}</Text>
              )}
              {complemento && <Text>{complemento}</Text>}
            </Flex>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default CabecalhoDoWizard;
